import axios, { AxiosError, CanceledError, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/auth'
import router from '@/router'
import { logAuthEvent } from './telemetry'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use(async (config) => {
  const authStore = useAuthStore()
  const token = await authStore.getAccessToken()
  if (!token) {
    // Do not fire a bearer-less request — it would produce a guaranteed 401 on the server
    // and add noise to telemetry. getAccessToken has already logged the root cause and, when
    // appropriate, kicked off acquireTokenRedirect.
    logAuthEvent({
      event: 'request_cancelled_no_token',
      url: config.url,
      hasAccount: authStore.isAuthenticated,
    })
    throw new CanceledError('No access token available')
  }
  config.headers.Authorization = `Bearer ${token}`
  return config
})

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

let refreshingToken: Promise<string | null> | null = null

async function refreshToken(): Promise<string | null> {
  if (refreshingToken) return refreshingToken
  const authStore = useAuthStore()
  refreshingToken = authStore.getAccessToken(true).finally(() => {
    refreshingToken = null
  })
  return refreshingToken
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Ignore locally-cancelled requests (we cancelled them ourselves in the request interceptor).
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    const original = error.config as RetryableConfig | undefined

    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true
      logAuthEvent({
        event: 'response_401_retry_attempt',
        url: original.url,
        status: 401,
      })
      const newToken = await refreshToken()
      if (newToken) {
        logAuthEvent({
          event: 'response_401_retry_token_acquired',
          url: original.url,
        })
        original.headers.Authorization = `Bearer ${newToken}`
        return api(original)
      }
      logAuthEvent({
        event: 'response_401_retry_no_token',
        url: original.url,
      })
      await router.push({ name: 'login' })
      return Promise.reject(error)
    }

    if (error.response?.status === 401) {
      logAuthEvent({
        event: 'response_401_after_retry',
        url: original?.url,
        status: 401,
      })
    }

    if (error.response?.status === 403) {
      await router.push({ name: 'access-denied' })
    }

    return Promise.reject(error)
  },
)

export default api
